/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Popover,
  Typography
} from "@mui/material";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

export const RenderMemberAliases = React.memo(({ data, handleDelete, canDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hiddenCards, setHiddenCards] = useState([]);
  const containerRef = useRef(null);
  const hoverTimeout = useRef(null);

  const calculateVisibleCards = useCallback(() => {
    if (!containerRef.current || !data?.length) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;

    const cardWidth = 100;
    const plusButtonWidth = 40;

    const newVisibleCards = [];
    const newHiddenCards = [];

    let maxVisible = 0;
    for (let i = 0; i < data.length; i++) {
      const requiredWidth = (i + 1) * cardWidth + (i < data.length - 1 ? plusButtonWidth : 0);
      if (requiredWidth <= containerWidth) {
        maxVisible = i + 1;
      } else {
        break;
      }
    }

    if (maxVisible === 0 && data.length > 0) {
      setVisibleCards([]);
      setHiddenCards(data);
      return;
    }

    const finalVisibleCount = Math.min(maxVisible, data.length);

    data.forEach((card, index) => {
      if (index < finalVisibleCount) {
        newVisibleCards.push(card);
      } else {
        newHiddenCards.push(card);
      }
    });

    setVisibleCards(newVisibleCards);
    setHiddenCards(newHiddenCards);
  }, [data]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCards();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateVisibleCards]);

  useEffect(() => {
    calculateVisibleCards();
  }, [data, calculateVisibleCards]);

  const handleOpenPopover = (event) => {
    if (hiddenCards?.length > 0) {
      clearTimeout(hoverTimeout.current);
      setAnchorEl(event.currentTarget);
      setIsPopoverOpen(true);
    }
  };

  const handleClosePopover = () => {
    hoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      setIsPopoverOpen(false);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    setIsPopoverOpen(true);
  };

  const handlePopoverMouseLeave = () => {
    handleClosePopover();
  };

  const open = Boolean(anchorEl) && isPopoverOpen;


  if (data?.length === 0) {
    return (
      <Chip
        sx={{
          backgroundColor: 'white',
          "&:hover": {
            backgroundColor: 'white',
          },
          height: '23px',
          border: '2px solid lightgray',
        }}
        label={'No aliases'}
      />
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: '50px',
        width: '100%'
      }}
    >
      {data?.map((item, index) => (
        <Chip
          key={index}
          sx={{
            marginRight: '3px',
            backgroundColor: '#e9f0f9',
            '&:hover': {
              backgroundColor: '#e9f0f9'
            },
            display: index < visibleCards.length ? 'inline-flex' : 'none',
            minWidth: 'auto',
            flexShrink: 0,
          }}
          icon={<LocalOfferOutlinedIcon sx={{ color: '#18181B', fontSize: '18px' }} />}
          label={item?.alias_raw}
          onDelete={canDelete ? () => handleDelete(item) : undefined}
        />
      ))}

      {hiddenCards.length > 0 && (
        <IconButton
          onMouseEnter={handleOpenPopover}
          sx={{
            height: '25px',
            width: '25px',
            backgroundColor: 'lightgray',
            "&:hover": {
              backgroundColor: 'lightgray'
            },
            flexShrink: 0,
            minWidth: '25px'
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            {`+${hiddenCards.length}`}
          </Typography>
        </IconButton>
      )}

      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '150px',
              borderRadius: '8px',
              overflow: 'hidden',
              marginTop: '5px',
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave
          }
        }}
        disableRestoreFocus
      >
        <Box sx={{ padding: '16px' }}>
          {hiddenCards?.length > 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {hiddenCards?.map((item, index) => (
              <Chip
                key={index}
                sx={{
                  justifyContent: 'space-between',
                  backgroundColor: '#e9f0f9',
                  '&:hover': {
                    backgroundColor: '#e9f0f9'
                  }
                }}
                icon={<LocalOfferOutlinedIcon sx={{ color: '#18181B', fontSize: '18px' }} />}
                label={item?.alias_raw}
                onDelete={canDelete ? () => handleDelete(item) : undefined}
              />
            ))}
          </Box>}
        </Box>
      </Popover>
    </Box>
  );
});

RenderMemberAliases.displayName = 'RenderMemberAliases'
